import { Circle, Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { type ReactNode } from 'react';
import { type Customer, type MealCategory, type ScheduledMeal } from '../../../entities';

import { type LocalDate } from '../../../utils/local-date';

const styles = StyleSheet.create({
  page: {
    padding: 20,
  },
  title: {
    fontSize: 20,
    marginBottom: 10,
    textAlign: 'center',
  },
  calendar: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderTop: '1px solid black',
    borderLeft: '1px solid black',
  },
  dayCell: {
    width: '14.28%',
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    padding: 5,
    minHeight: 100,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  dayHeader: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 5,
    textAlign: 'right',
    padding: 3,
  },
  cellData: {
    fontSize: 10,
    marginBottom: 2,
    paddingLeft: 10,
  },
  cellDataHeader: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  dayNamesHeader: {
    flexDirection: 'row',
    textAlign: 'center',
    backgroundColor: '#e6e6e6',
    paddingVertical: 5,
    fontWeight: 'bold',
  },
  dayName: {
    width: '14.28%',
    fontSize: 10,
    borderRight: '1px solid black',
    padding: 3,
  },
});

const generateCalendarData = (startDate: LocalDate, endDate: LocalDate): LocalDate[] => {
  const months = [];
  let currentMonth = startDate.startOf('month');
  while (currentMonth.isBefore(endDate, 'month') || currentMonth.isSame(endDate, 'month')) {
    months.push(currentMonth);
    currentMonth = currentMonth.add(1, 'month');
  }
  return months;
};

const getDaysInMonth = (month: LocalDate): Array<number | null> => {
  const days = [];
  const startDay = month.startOf('month').day();
  const totalDays = month.daysInMonth();
  for (let i = 0; i < startDay; i++) {
    days.push(null);
  }

  for (let day = 1; day <= totalDays; day++) {
    days.push(day);
  }

  return days;
};

const renderCellData = (
  day: number | null,
  data: any,
  showMealItemPortions: boolean
): ReactNode => {
  if (!day) return null;
  return data.map((item: any, idx: number) => (
    <>
      <Text key={idx} style={styles.cellDataHeader}>
        {item.name === undefined || item.name === '' ? item.mealCategory.name : item.name}
      </Text>
      {item.mealItemsNames.map((mi: string, amiidx: number) => (
        <Text style={styles.cellData} key={amiidx}>
          {
            // the format of the mi is "x unit of meal item "
            // mi is splited by " of " and then joined by " of " again to remove the first part which is the quantity and unit
            // as the meal item name may contain " of " in its name, we need to join the rest of the parts back together
          }
          {showMealItemPortions ? mi : mi.split(' of ').slice(1).join(' of ')}
        </Text>
      ))}
    </>
  ));
};

const DayNamesHeader = (): ReactNode => (
  <View style={styles.dayNamesHeader}>
    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName, index) => (
      <Text key={index} style={styles.dayName}>
        {dayName}
      </Text>
    ))}
  </View>
);

const SiteMealCategoryMenuPdf = ({
  startDate,
  endDate,
  data,
  site,
  mealCategory,
  customer,
  showMealItemPortions,
}: {
  startDate: LocalDate;
  endDate: LocalDate;
  data: any;
  site: string;
  mealCategory: MealCategory;
  customer: Customer;
  showMealItemPortions: boolean;
}): ReactNode => {
  const months = generateCalendarData(startDate, endDate);
  return (
    <Document>
      {months.map((month) => (
        <Page orientation="landscape" size={'A3'} key={month.format('YYYY-MM')} style={styles.page}>
          <View
            style={{
              marginLeft: 30,
              marginRight: 30,
              flexDirection: 'row',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Text style={styles.title}>{month.format('MMMM YYYY')}</Text>

            <Text style={styles.title}>{customer.name}</Text>
            <Text style={styles.title}>{customer.sites.find((s) => s.id === site)?.name}</Text>
            <Text style={styles.title}>{mealCategory.name}</Text>
          </View>
          <Circle r="5px" style={{ color: 'black' }} />
          <DayNamesHeader />

          <View style={styles.calendar}>
            {getDaysInMonth(month).map((day, idx) => (
              <View key={idx} style={styles.dayCell}>
                <Text style={styles.dayHeader}>{day}</Text>
                {renderCellData(
                  day,
                  data.filter((ms: ScheduledMeal) => {
                    const d = month.add((day ?? 0) - 1, 'day');
                    return ms.day.isSame(d, 'day');
                  }),
                  showMealItemPortions
                )}
              </View>
            ))}
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default SiteMealCategoryMenuPdf;
